import Vue from 'vue';
import { DRAWING_TOOLS, COLORS, SHAPES, SHAPE_ACTIONS } from '../../models/drawingTools';
import { Canvas, PencilBrush } from 'fabric';

export function createDrawingTools() {
	const DEFAULT_BRUSH_WIDTH = 10;
	const DEFAULT_STROKE_WIDTH = 10;
	const DEFAULT_ERASER_SIZE = 24;

	return new Vue({
		data() {
			return {
				isDrawingToolActive: false,
				selectedDrawingTool: null,
				pencilBrush: null,
				pencilBrushColor: COLORS.RED,
				pencilBrushWidth: DEFAULT_BRUSH_WIDTH,
				shapeStrokeColor: COLORS.RED,
				shapeStrokeWidth: DEFAULT_STROKE_WIDTH,
				shapeFillColor: COLORS.TRANSPARENT,
				selectedShape: SHAPES.RECTANGLE,
				currentDrawingShape: null,
				isDrawingShape: false,
				startPointShape: null,
				eraserSize: DEFAULT_ERASER_SIZE,
				drawingCanvas: null,
				drawingCanvasElement: null,
				cursorElement: null,
				resizeObserver: null
			};
		},
		computed: {
			isPencilEnabled() {
				return this.isDrawingToolActive && this.selectedDrawingTool === DRAWING_TOOLS.PENCIL;
			},
			isShapeEnabled() {
				return this.isDrawingToolActive && this.selectedDrawingTool === DRAWING_TOOLS.SHAPE;
			},
			isEraserEnabled() {
				return this.isDrawingToolActive && this.selectedDrawingTool === DRAWING_TOOLS.ERASER;
			}
		},
		methods: {
			initDrawingTools(drawingCanvasRef, wrapperCanvasSelector, slideSelector, cursorSelector) {
				if (!drawingCanvasRef) return;

				this.drawingCanvas = new Canvas(drawingCanvasRef, {
					backgroundColor: 'transparent',
					freeDrawingCursor: 'none',
					hoverCursor: 'default',
					perPixelTargetFind: true,
					preserveObjectStacking: true,
					selection: false,
					skipTargetFind: true,
					targetFindTolerance: this.eraserSize / 2
				});

				this.drawingCanvasElement = this.drawingCanvas.elements.container;

				if (!this.drawingCanvasElement) return;

				this.$startDrawingCanvasResize(wrapperCanvasSelector, slideSelector);
				this.$startDrawingCanvasCustomCursor(cursorSelector);
			},
			destroyDrawingTools() {
				if (!this.drawingCanvas || !this.drawingCanvasElement) return;

				this.drawingCanvas.dispose();

				this.$stopDrawingCanvasResize();
				this.$stopDrawingCanvasCustomCursor();
				this.$resetDrawingToolsState();
			},
			toggleDrawingToolActive() {
				this.isDrawingToolActive = !this.isDrawingToolActive;

				if (this.isDrawingToolActive && !this.selectedDrawingTool) this.selectedDrawingTool = DRAWING_TOOLS.PENCIL;
			},
			selectDrawingTool(drawingTool) {
				if (drawingTool === DRAWING_TOOLS.CLEAR) {
					this.clearCanvas();
					return;
				}

				this.isDrawingToolActive = true;
				this.selectedDrawingTool = drawingTool;
			},
			clearCanvas() {
				if (!this.drawingCanvas) return;

				this.drawingCanvas.clear();
			},
			selectPencilBrushColor(color) {
				this.pencilBrushColor = color;
				this.pencilBrush.color = color;
			},
			updatePencilBrushWidth(width) {
				this.pencilBrushWidth = width;
				this.pencilBrush.width = width;
			},
			selectShapeStrokeColor(color) {
				this.shapeStrokeColor = color;
			},
			updateShapeStrokeWidth(width) {
				this.shapeStrokeWidth = width;
			},
			selectShapeFillColor(color) {
				this.shapeFillColor = color;
			},
			selectShape(shape) {
				this.selectedShape = shape;
			},
			$resetDrawingToolsState() {
				this.drawingCanvasElement = null;

				this.isDrawingToolActive = false;
				this.selectedDrawingTool = null;

				this.pencilBrush = null;
				this.pencilBrushColor = COLORS.RED;
				this.pencilBrushWidth = DEFAULT_BRUSH_WIDTH;

				this.shapeStrokeColor = COLORS.RED;
				this.shapeStrokeWidth = DEFAULT_STROKE_WIDTH;
				this.shapeFillColor = COLORS.TRANSPARENT;
				this.selectedShape = SHAPES.RECTANGLE;

				this.eraserSize = DEFAULT_ERASER_SIZE;
			},
			$startDrawingCanvasResize(wrapperCanvasSelector, slideSelector) {
				const wrapperCanvasElement = document.querySelector(wrapperCanvasSelector);

				if (!wrapperCanvasElement) return;

				this.$updateDrawingCanvasResize(wrapperCanvasElement, slideSelector);

				this.resizeObserver = new ResizeObserver(() =>
					this.$updateDrawingCanvasResize(wrapperCanvasElement, slideSelector)
				);
				this.resizeObserver.observe(wrapperCanvasElement);
			},
			$stopDrawingCanvasResize() {
				if (!this.resizeObserver) return;

				this.resizeObserver.disconnect();
			},
			$updateDrawingCanvasResize(wrapperCanvasElement, slideSelector) {
				const slideElement = document.querySelector(slideSelector);

				if (!slideElement) return;

				const resizeScale = Math.min(
					wrapperCanvasElement.offsetWidth / (slideElement.offsetWidth + 30),
					wrapperCanvasElement.offsetHeight / (slideElement.offsetHeight + 30)
				);
				const translateX = (wrapperCanvasElement.offsetWidth / 2 - slideElement.offsetWidth / 2) / resizeScale;
				const translateY = (wrapperCanvasElement.offsetHeight / 2 - slideElement.offsetHeight / 2) / resizeScale;

				this.drawingCanvasElement.style.transform = `scale(${resizeScale}) translate(${translateX}px, ${translateY}px)`;
			},
			$startDrawingCanvasCustomCursor(cursorSelector) {
				this.cursorElement = document.querySelector(cursorSelector);

				if (!this.cursorElement) return;

				this.drawingCanvasElement.addEventListener('pointerenter', this.$onCanvasMouseEnter);
				this.drawingCanvasElement.addEventListener('pointerleave', this.$onCanvasMouseLeave);
				this.drawingCanvasElement.addEventListener('pointermove', this.$onCanvasMouseMove);
			},
			$stopDrawingCanvasCustomCursor() {
				this.drawingCanvasElement.removeEventListener('pointerenter', this.$onCanvasMouseEnter);
				this.drawingCanvasElement.removeEventListener('pointerleave', this.$onCanvasMouseLeave);
				this.drawingCanvasElement.removeEventListener('pointermove', this.$onCanvasMouseMove);
			},
			$onCanvasMouseEnter() {
				const isCustomCursorToolEnabled = this.isPencilEnabled || this.isEraserEnabled;

				if (!isCustomCursorToolEnabled) {
					this.cursorElement.style.display = 'none';
					return;
				}

				const size = this.isPencilEnabled ? this.pencilBrushWidth : this.eraserSize;

				Object.assign(this.cursorElement.style, {
					display: 'block',
					backgroundColor: this.isPencilEnabled ? this.pencilBrushColor : 'white',
					width: `${size}px`,
					height: `${size}px`
				});
			},
			$onCanvasMouseLeave() {
				this.cursorElement.style.display = 'none';
			},
			$onCanvasMouseMove(event) {
				this.cursorElement.style.left = event.clientX + 'px';
				this.cursorElement.style.top = event.clientY + 'px';
			},
			$initPencilBrush() {
				this.pencilBrush = new PencilBrush(this.drawingCanvas);
				this.pencilBrush.width = this.pencilBrushWidth;
				this.pencilBrush.color = this.pencilBrushColor;

				this.drawingCanvas.freeDrawingBrush = this.pencilBrush;
			},
			$startDrawingShape() {
				this.drawingCanvas.on('mouse:down', this.$initShape);
				this.drawingCanvas.on('mouse:move', this.$resizeShape);
				this.drawingCanvas.on('mouse:up', this.$endShape);
			},
			$stopDrawingShape() {
				this.drawingCanvas.off('mouse:down', this.$initShape);
				this.drawingCanvas.off('mouse:move', this.$resizeShape);
				this.drawingCanvas.off('mouse:up', this.$endShape);
			},
			$initShape(event) {
				const viewportPoint = this.drawingCanvas.getViewportPoint(event);

				this.isDrawingShape = true;
				this.startPointShape = viewportPoint;

				const commonProps = {
					left: this.startPointShape.x,
					top: this.startPointShape.y,
					originX: 'left',
					originY: 'top',
					fill: this.shapeFillColor,
					stroke: this.shapeStrokeColor,
					strokeWidth: this.shapeStrokeWidth,
					hasControls: false
				};

				this.currentDrawingShape = SHAPE_ACTIONS[this.selectedShape].create(commonProps);

				this.drawingCanvas.add(this.currentDrawingShape);
			},
			$resizeShape(event) {
				if (!this.isDrawingShape) return;

				const scenePoint = this.drawingCanvas.getScenePoint(event);

				const deltaX = scenePoint.x - this.startPointShape.x;
				const deltaY = scenePoint.y - this.startPointShape.y;

				const width = Math.abs(deltaX);
				const height = Math.abs(deltaY);

				const left = deltaX < 0 ? scenePoint.x : this.startPointShape.x;
				const top = deltaY < 0 ? scenePoint.y : this.startPointShape.y;

				SHAPE_ACTIONS[this.selectedShape].resize(this.currentDrawingShape, left, top, width, height);

				this.currentDrawingShape.setCoords();
				this.drawingCanvas.requestRenderAll();
			},
			$endShape() {
				this.isDrawingShape = false;
				this.currentDrawingShape = null;
			},
			$startDrawingCanvasEraser() {
				this.drawingCanvas.on('mouse:down', this.$startErase);
				this.drawingCanvas.on('mouse:move', this.$moveErase);
				this.drawingCanvas.on('mouse:up', this.$stopErase);
			},
			$stopDrawingCanvasEraser() {
				this.drawingCanvas.on('mouse:down', this.$startErase);
				this.drawingCanvas.on('mouse:move', this.$moveErase);
				this.drawingCanvas.on('mouse:up', this.$stopErase);
			},
			$startErase(event) {
				if (!this.isEraserEnabled) return;

				this.isErasing = true;

				this.$eraseStrokeAt(event);
			},
			$moveErase(event) {
				if (!this.isErasing) return;

				this.$eraseStrokeAt(event);
			},
			$stopErase() {
				this.isErasing = false;
			},
			$eraseStrokeAt(event) {
				const canvasObject = this.drawingCanvas.findTarget(event, true);
				const target = canvasObject.target;

				if (target) {
					this.drawingCanvas.remove(target);
					this.drawingCanvas.requestRenderAll();
				}
			},
			$updateDrawingCanvasCursor() {
				const cursorConfig = {
					[DRAWING_TOOLS.SHAPE]: {
						defaultCursor: 'crosshair',
						hoverCursor: 'default'
					},
					[DRAWING_TOOLS.ERASER]: {
						defaultCursor: 'none',
						hoverCursor: 'none'
					}
				};

				const defaultCursorConfig = {
					defaultCursor: 'default',
					hoverCursor: 'default'
				};

				if (!this.isDrawingToolActive) {
					Object.assign(this.drawingCanvas, defaultCursorConfig);
					return;
				}

				const selectedCursorConfig = cursorConfig[this.selectedDrawingTool] || defaultCursorConfig;
				Object.assign(this.drawingCanvas, selectedCursorConfig);
			}
		},
		watch: {
			isPencilEnabled() {
				this.drawingCanvas.isDrawingMode = this.isPencilEnabled;

				if (this.isPencilEnabled && !this.pencilBrush) this.$initPencilBrush();
			},
			isShapeEnabled() {
				if (!this.drawingCanvas) return;

				this.$updateDrawingCanvasCursor();

				if (this.isShapeEnabled) {
					this.$startDrawingShape();
					return;
				}

				this.$stopDrawingShape();
			},
			isEraserEnabled() {
				if (!this.drawingCanvas) return;

				this.$updateDrawingCanvasCursor();
				this.drawingCanvas.skipTargetFind = !this.isEraserEnabled;

				if (this.isEraserEnabled) {
					this.$startDrawingCanvasEraser();
					return;
				}

				this.$stopDrawingCanvasEraser();
			}
		}
	});
}
